import {debounce, uniq} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import Loading from '../../common/component/Loading';
import SingleText from '../../common/component/SingleText';
import {Page} from '../../common/constant/page';
import useLocalStorage from '../../common/hook/useLocalStorage';
import useTypedSelector from '../../common/hook/useTypedSelector';
import {useFetchInfo} from '../../common/util/fetch';
import {PageInfo, routeTo} from '../../common/util/PageRouter';
import MaxWidth from '../../commonSub/component/MaxWidth';
import NoItems from '../../commonSub/component/NoItems';
import {sendSearchEventV4} from '../common';
import RecentListV4 from '../component/RecentListV4';
import ResultV4 from '../component/ResultV4';
import SearchInputV4 from '../component/SearchInputV4';
import SuggestListV4 from '../component/SuggestListV4';
import useSearchPageViewV4 from '../hook/useSearchPageViewV4';
import {actions, ActionType} from '../state/action';
import {ISearchType, IViewTypeV4} from '../type';

interface IProps {
  initialKeyword: string;
  type: ISearchType;
}

export default function SearchV4({initialKeyword, type}: IProps) {
  const [keyword, setKeyword] = useState(initialKeyword);
  const [viewType, setViewType] = useState<IViewTypeV4>(
    initialKeyword ? 'resultView' : 'searchView'
  );

  const dispatch = useDispatch();

  const [recentKeywords, setRecentKeywords] = useLocalStorage<string[]>({
    key: 'search-recent-keywords',
    initialValue: [],
  });

  const [suggestKeywords, result] = useTypedSelector(state => [
    state.search.suggest[keyword],
    state.search.result,
  ]);

  const {isFetching, isFetched, isFirstFetched} = useFetchInfo(
    ActionType.FetchResult
  );

  const {totalCount} = result;
  const isSearchView = viewType === 'searchView';
  const isResultView = viewType === 'resultView';

  const deleteRecentKeyword = (keyword: string) => {
    const newRecentKeywords = recentKeywords.filter(
      recentKeyword => recentKeyword !== keyword
    );
    setRecentKeywords(newRecentKeywords);
  };

  const fetchSugget = useMemo(
    () =>
      debounce((keyword: string) => {
        dispatch(actions.fetchSuggest(keyword));
      }, DEBOUNCE_DELAY),
    [dispatch]
  );

  const getResult = useCallback(
    (keyword: string, type?: ISearchType) => {
      dispatch(actions.fetchResult({keyword, type}, true));
      setViewType('resultView');
      setKeyword(keyword);
    },
    [dispatch]
  );

  const search = useCallback(
    (keyword: string) => {
      if (!keyword || isFetching) {
        return;
      }

      const pageInfo: PageInfo = {
        page: Page.Search,
        keyword: encodeURIComponent(keyword),
      };

      const newRecentKeywords = uniq([keyword, ...recentKeywords]);
      setRecentKeywords(newRecentKeywords.slice(0, MAX_RECENT_KEYWORDS_SIZE));

      getResult(keyword);
      routeTo(pageInfo);
    },
    [isFetching, recentKeywords, setRecentKeywords, getResult]
  );

  useEffect(() => {
    if (initialKeyword) {
      getResult(initialKeyword, type);
    }
  }, [getResult, initialKeyword, type]);

  useEffect(() => {
    if (keyword && isSearchView) {
      fetchSugget(keyword);
    }
  }, [fetchSugget, isSearchView, keyword]);

  useSearchPageViewV4({viewType, keyword});

  return (
    <MaxWidth className="!px-0">
      <SearchInputV4
        keyword={keyword}
        setKeyword={keyword => setKeyword(keyword)}
        viewType={viewType}
        setViewType={viewType => setViewType(viewType)}
        totalCount={totalCount}
        search={keyword => {
          sendSearchEventV4({
            viewType,
            actionName: 'search',
            customProps: {
              event_layer: 'search',
              input_keyword: keyword,
            },
          });
          search(keyword);
        }}
        onFocus={() => {
          isResultView &&
            sendSearchEventV4({
              viewType,
              actionName: 'search_box',
              customProps: {
                event_layer: 'search_box',
                search_keyword: keyword,
                search_result_count: totalCount,
              },
            });
          setViewType('searchView');
        }}
        onClear={() => {
          sendSearchEventV4({
            viewType,
            actionName: 'search_keyword_delete',
            customProps: {
              event_layer: 'search_box',
              ...(isResultView && {
                search_keyword: keyword,
                search_result_count: totalCount,
              }),
            },
          });
        }}
      />
      {isSearchView && keyword.length === 0 && recentKeywords.length > 0 && (
        <div className="pt-64">
          <RecentListV4
            keywords={recentKeywords}
            deleteKeyword={deleteRecentKeyword}
            search={keyword => {
              search(keyword);
            }}
          />
        </div>
      )}
      {isSearchView && keyword.length > 0 && (
        <div className="pt-64">
          <SuggestListV4
            keyword={keyword}
            suggestKeywords={suggestKeywords}
            search={keyword => {
              search(keyword);
            }}
          />
        </div>
      )}
      {isFirstFetched && isResultView && totalCount > 0 && (
        <ResultV4 result={result} keyword={keyword} type={type} />
      )}
      {isFetched && isResultView && totalCount === 0 && (
        <NoItems
          textKey="search_result_empty_web"
          className="flex-col leading-20"
          beforeElement={
            <SingleText className="s16-regular-grey08 text-center">
              "{keyword.substring(0, MAX_NO_ITEM_KEYWORD_SIZE)}
              {keyword.length > MAX_NO_ITEM_KEYWORD_SIZE && '...'}"
            </SingleText>
          }
        />
      )}
      {!isFirstFetched && isResultView && <Loading isFull={true} />}
    </MaxWidth>
  );
}

const DEBOUNCE_DELAY = 500;
const MAX_RECENT_KEYWORDS_SIZE = 10;
const MAX_NO_ITEM_KEYWORD_SIZE = 17;
